import { useState, useEffect } from "react";
import { toast } from "sonner";
import { customCollectionsService } from "../../services/customCollections";
import { getImageUrl } from "../../utils/imageUtils";
import api from "../../services/api";
import { useModules } from "../../context/ModuleContext";
import ModuleReadOnlyNotice from "../../components/reseller/ModuleReadOnlyNotice";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
} from "@/components/ui/sheet";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { Plus, Pencil, Trash2, Layers, Search, Upload, X } from "lucide-react";

export default function ResellerCollections() {
    // eDisplayroom off: collections stay listed, but every path into the edit
    // sheet is removed, which is what makes its internal writes unreachable.
    const { canWrite } = useModules();
    const readOnly = !canWrite("edisplayroom");
    const [collections, setCollections] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [sheetOpen, setSheetOpen] = useState(false);
    const [editingCollection, setEditingCollection] = useState(null);

    const [formData, setFormData] = useState({
        name: "",
        description: "",
        cover_image: "",
        is_active: true,
        is_featured: false
    });

    useEffect(() => {
        fetchCollections();
    }, []);

    const fetchCollections = async () => {
        try {
            setLoading(true);
            const response = await customCollectionsService.getAll();
            setCollections(response.data || []);
        } catch (error) {
            toast.error("Failed to fetch collections");
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (collection) => {
        setEditingCollection(collection);
        setFormData({
            name: collection.name,
            description: collection.description || "",
            cover_image: collection.cover_image || "",
            is_active: collection.is_active,
            is_featured: collection.is_featured || false
        });
        setSheetOpen(true);
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure you want to delete this collection?")) return;
        try {
            await customCollectionsService.delete(id);
            toast.success("Collection deleted");
            fetchCollections();
        } catch (error) {
            toast.error("Failed to delete collection");
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const payload = { ...formData };
            if (editingCollection) {
                await customCollectionsService.update(editingCollection.collection_id || editingCollection._id, payload);
                toast.success("Collection updated successfully");
            } else {
                await customCollectionsService.create(payload);
                toast.success("Collection created successfully");
            }
            setSheetOpen(false);
            resetForm();
            fetchCollections();
        } catch (error) {
            console.error("Failed to save collection:", error);
            const errorMessage = error.response?.data?.message || "Failed to save collection";
            toast.error(errorMessage);
        }
    };

    const resetForm = () => {
        setEditingCollection(null);
        setFormData({
            name: "",
            description: "",
            cover_image: "",
            is_active: true,
            is_featured: false
        });
    };

    const openNewSheet = () => {
        resetForm();
        setSheetOpen(true);
    };

    const filteredCollections = collections.filter(c =>
        c.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (loading) return <div className="p-8 text-center text-slate-500">Loading collections...</div>;

    return (
        <div className="space-y-6 animate-fade-in">
            <ModuleReadOnlyNotice module="edisplayroom" />

            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                <div>
                    <h2 className="text-2xl font-bold text-slate-900">
                        My Collections
                    </h2>
                    <p className="text-slate-500">Organize your products into My Collections</p>
                </div>
                {!readOnly && (
                <Button onClick={openNewSheet} className="bg-indigo-600 hover:bg-indigo-700">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Collection
                </Button>
                )}
                <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
                    <SheetContent className="w-full sm:max-w-md overflow-y-auto">
                        <SheetHeader>
                            <SheetTitle>{editingCollection ? "Edit Collection" : "New Collection"}</SheetTitle>
                        </SheetHeader>
                        <form onSubmit={handleSubmit} className="space-y-4 mt-6">
                            <div className="space-y-2">
                                <Label htmlFor="name">Collection Name</Label>
                                <Input
                                    id="name"
                                    required
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    placeholder="e.g. Summer Sale, New Arrivals"
                                />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="description">Description</Label>
                                <Textarea
                                    id="description"
                                    value={formData.description}
                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                    rows={3}
                                />
                            </div>

                            <div className="space-y-2">
                                <Label>Cover Image</Label>
                                <div className="border-2 border-dashed border-slate-200 rounded-xl p-4 text-center hover:bg-slate-50 transition-colors cursor-pointer relative">
                                    <input
                                        type="file"
                                        accept="image/*"
                                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                        onChange={async (e) => {
                                            const file = e.target.files[0];
                                            if (!file) return;

                                            const fd = new FormData();
                                            fd.append('image', file);

                                            try {
                                                const res = await api.post(`/upload/collection-image`, fd, {
                                                    headers: { 'Content-Type': 'multipart/form-data' }
                                                });
                                                setFormData({ ...formData, cover_image: res.data.path });
                                                toast.success("Image uploaded!");
                                            } catch (err) {
                                                toast.error("Upload failed");
                                            }
                                        }}
                                    />
                                    {formData.cover_image ? (
                                        <div className="relative group aspect-video rounded-lg overflow-hidden border border-slate-200">
                                            <img src={getImageUrl(formData.cover_image)} alt="Cover" className="w-full h-full object-cover" />
                                            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                                                <Upload className="w-8 h-8 text-white" />
                                            </div>
                                            <button
                                                type="button"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setFormData({ ...formData, cover_image: "" });
                                                }}
                                                className="absolute top-2 right-2 p-1.5 bg-white/90 rounded-full text-slate-600 hover:text-red-600 shadow-sm opacity-0 group-hover:opacity-100 transition-opacity z-10"
                                            >
                                                <X className="w-4 h-4" />
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="flex flex-col items-center gap-2 text-slate-500 py-4">
                                            <Upload className="w-8 h-8" />
                                            <p className="text-sm font-medium">Click to upload cover image</p>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="flex items-center space-x-6 pt-2">
                                <div className="flex items-center space-x-2">
                                    <Checkbox
                                        id="is_active"
                                        checked={formData.is_active}
                                        onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                                    />
                                    <Label htmlFor="is_active" className="cursor-pointer">Active</Label>
                                </div>
                                <div className="flex items-center space-x-2">
                                    <Checkbox
                                        id="is_featured"
                                        checked={formData.is_featured}
                                        onCheckedChange={(checked) => setFormData({ ...formData, is_featured: checked })}
                                    />
                                    <Label htmlFor="is_featured" className="cursor-pointer">Featured</Label>
                                </div>
                            </div>

                            <Button type="submit" className="w-full bg-indigo-600 hover:bg-indigo-700 mt-4">
                                {editingCollection ? "Update Collection" : "Create Collection"}
                            </Button>
                        </form>
                    </SheetContent>
                </Sheet>
            </div>

            <div className="flex items-center gap-2 max-w-sm">
                <div className="relative flex-1">
                    <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
                    <Input
                        placeholder="Search collections..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-9"
                    />
                </div>
            </div>

            <div className="card-elevated overflow-hidden">
                <Table>
                    <TableHeader>
                        <TableRow className="bg-slate-50">
                            <TableHead className="w-[80px]">Image</TableHead>
                            <TableHead>Created At</TableHead>
                            <TableHead>Collection Name</TableHead>
                            <TableHead>Description</TableHead>
                            <TableHead>Products</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {filteredCollections.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                                    No collections found. Start by creating one!
                                </TableCell>
                            </TableRow>
                        ) : (
                            filteredCollections.map((col) => (
                                <TableRow key={col._id || col.collection_id}>
                                    <TableCell>
                                        {col.cover_image ? (
                                            <img src={getImageUrl(col.cover_image)} alt="" className="w-10 h-10 object-cover rounded" />
                                        ) : (
                                            <div className="w-10 h-10 bg-slate-100 rounded flex items-center justify-center text-slate-400">
                                                <Layers className="w-5 h-5" />
                                            </div>
                                        )}
                                    </TableCell>
                                    <TableCell className="text-xs text-slate-500">
                                        {new Date(col.createdAt).toLocaleDateString()}
                                    </TableCell>
                                    <TableCell className="font-medium text-slate-900">
                                        {col.name}
                                        {col.is_featured && <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-amber-50 text-amber-600 border border-amber-100 uppercase tracking-wider">Featured</span>}
                                    </TableCell>
                                    <TableCell className="max-w-xs truncate text-slate-500">{col.description}</TableCell>
                                    <TableCell>
                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-800">
                                            {col.product_count || 0}
                                        </span>
                                    </TableCell>
                                    <TableCell>
                                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${col.is_active
                                            ? "bg-green-100 text-green-700"
                                            : "bg-slate-100 text-slate-600"
                                            }`}>
                                            {col.is_active ? "Active" : "Inactive"}
                                        </span>
                                    </TableCell>
                                    <TableCell className="text-right">
                                        <div className="flex justify-end gap-2">
                                            {!readOnly && (<>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => handleEdit(col)}
                                            >
                                                <Pencil className="w-4 h-4" />
                                            </Button>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => handleDelete(col.collection_id || col._id)}
                                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </Button>
                                            </>)}
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </div>
        </div>
    );
}
